// ============================================================
// CommandBar — die Kommandoleiste der App (F, "/", ⌘K).
//
// Navigation + Projektwechsel mit Tastatursteuerung, dazu die Zeile
// "Spielplatz-KI fragen": wo ein Assistent auf der Seite haengt (Dashboard),
// reicht `onAsk` die Frage an ihn weiter; sonst bleibt der ehrliche
// "in Vorbereitung"-Zustand.
//
// Zwei Erscheinungsformen:
//   hero   — die grosse Leiste ueber den Kennzahlen des Dashboards,
//   hidden — nur Tastenkuerzel + Fenster, kein sichtbarer Ausloeser.
// Eine Header-Pille gab es frueher; sie blitzte beim Routenwechsel auf und
// ist ersatzlos entfallen (Nutzerfeedback 22.08.).
// ============================================================
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/http";
import { Icon, portalRoot } from "../components/ui";
import { useStore } from "../store/useStore";
import { warmProjekt, warmRouteChunk } from "./ProjectSwitcher";

export interface Cmd {
  id: string;
  icon: string;
  label: string;
  hint?: string;
  section: string;
  run: () => void;
  /** Vorgriff: wird gerufen, sobald die Zeile ins Auge faellt (Zeiger oder
   *  Pfeiltaste) — lange bevor jemand Enter drueckt. Optional, damit die
   *  Kartensuche dieselbe Liste weiterverwenden kann, ohne etwas zu tun. */
  warm?: () => void;
}


/**
 * Befehle der App — Navigation, Projekte, Theme. Geteilt zwischen der
 * Hero-Suche (Dashboard) und der Kartensuche, damit die Karte die App aus EINEM
 * Feld heraus steuern kann. onDone schliesst den jeweiligen Aufrufer.
 */
export function useAppCommands(onDone: () => void, enabled = true): Cmd[] {
  const auth = useStore((s) => s.auth);
  const lang = useStore((s) => s.lang);
  const toggleTheme = useStore((s) => s.toggleTheme);
  const lastProjectId = useStore((s) => s.lastProjectId);
  const setLastProject = useStore((s) => s.setLastProject);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const projectsQ = useQuery({ queryKey: ["projects"], queryFn: () => api.projects.list(), enabled: !!auth && enabled, staleTime: 60_000 });
  const de = lang === "de";
  return useMemo<Cmd[]>(() => {
    if (!auth) return [];
    const nav = (to: string) => () => {
      onDone();
      navigate(to);
    };
    /* Vorgriff je Zeile — in der Kommandoleiste ist die Vorlaufzeit besonders
       lang: erst tippen, dann mit den Pfeiltasten wandern, dann Enter. Wer die
       Zeile auch nur streift, hat beim Ausloesen alles beisammen. */
    const base: Cmd[] = [
      { id: "karte", icon: "map-trifold", label: de ? "Karte öffnen" : "Open map", section: de ? "Navigation" : "Navigation", run: nav(lastProjectId ? `/karte?project=${lastProjectId}` : "/karte"), warm: () => { warmRouteChunk("karte"); warmProjekt(qc, lastProjectId, false); } },
      { id: "dashboard", icon: "squares-four", label: "Dashboard", section: "Navigation", run: nav("/"), warm: () => { warmRouteChunk("stats"); warmProjekt(qc, lastProjectId); } },
      ...(lastProjectId ? [{ id: "stats", icon: "chart-bar", label: de ? "Auswertungen & Diagramme" : "Statistics & charts", section: "Navigation", run: nav(`/project/${lastProjectId}`), warm: () => { warmRouteChunk("stats"); warmProjekt(qc, lastProjectId); } }] : []),
      ...(auth.role === "admin" ? [{ id: "admin", icon: "sliders-horizontal", label: de ? "Admin-Panel" : "Admin panel", section: "Navigation", run: nav("/admin"), warm: () => warmRouteChunk("admin") }] : []),
      ...(auth.role === "admin" ? [{ id: "overview", icon: "squares-four", label: de ? "Projektübersicht" : "Project overview", section: "Navigation", run: nav("/overview") }] : []),
      { id: "konto", icon: "user-circle", label: de ? "Mein Konto" : "My account", section: "Navigation", run: nav("/account"), warm: () => warmRouteChunk("konto") },
      { id: "meldeportal", icon: "chat-circle-dots", label: de ? "Meldeportal: Meldung erfassen" : "Report portal: new report", section: "Navigation", run: nav("/meldeportal") },
      { id: "wuensche", icon: "sparkle", label: de ? "Funktionswünsche: einreichen und abstimmen" : "Feature requests: submit and vote", section: "Navigation", run: nav("/wuensche") },
      { id: "theme", icon: "moon", label: de ? "Hell/Dunkel wechseln" : "Toggle theme", section: "Navigation", run: () => { toggleTheme(); onDone(); } },
    ];
    const projects: Cmd[] = (projectsQ.data ?? []).map((p) => ({
      id: `proj-${p.id}`,
      icon: "map-pin-area",
      label: p.name,
      hint: de ? "Karte öffnen" : "Open map",
      section: de ? "Projekte" : "Projects",
      // Projektzeilen fuehren auf die KARTE — also Kartenchunk, ohne stats
      warm: () => { warmRouteChunk("karte"); warmProjekt(qc, p.id, false); },
      run: () => {
        setLastProject(p.id);
        onDone();
        navigate(`/karte?project=${p.id}`);
      },
    }));
    return [...base, ...projects];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth, projectsQ.data, lastProjectId, de, navigate, qc, setLastProject, toggleTheme]);
}

export function CommandBar({
  variant = "hidden",
  onAsk,
}: {
  /** hero = grosse Leiste im Dashboard, hidden = nur Tastenkuerzel + Fenster */
  variant?: "hero" | "hidden";
  /** Frage an den Assistenten der Seite reichen (Dashboard: ChatWidget) */
  onAsk?: (q: string) => void;
}) {
  const auth = useStore((s) => s.auth);
  const lang = useStore((s) => s.lang);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(0);
  const [aiAsked, setAiAsked] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);


  // F (wie "Fragen/Finden") und "/" oeffnen die Leiste — dieselben Tasten wie die
  // Kartensuche, nur wenn gerade nicht getippt wird; ⌘K/Ctrl+K bleibt als
  // Zweitweg fuer Tastatur-Gewohnheiten erhalten.
  useEffect(() => {
    const typing = (t: EventTarget | null) => {
      const el = t as HTMLElement | null;
      return !!el && (/^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName) || el.isContentEditable);
    };
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      } else if ((e.key.toLowerCase() === "f" || e.key === "/") && !e.metaKey && !e.ctrlKey && !e.altKey && !typing(e.target)) {
        e.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (open) {
      setQ("");
      setSel(0);
      setAiAsked(null);
      // nach der Eintritts-Animation fokussieren
      const t = window.setTimeout(() => inputRef.current?.focus(), 30);
      return () => window.clearTimeout(t);
    }
  }, [open]);

  const de = lang === "de";
  const cmds = useAppCommands(() => setOpen(false), open);

  const needle = q.trim().toLowerCase();
  // „Assistent öffnen" (ohne Frage): holt ein geschlossenes Chatfenster samt Verlauf zurueck —
  // ohne Launcher-Knopf im Dashboard ist das sonst nur ueber eine neue Frage moeglich
  const openAi: Cmd[] = onAsk
    ? [{ id: "ai-open", icon: "sparkle", label: de ? "KI-Assistent öffnen" : "Open AI assistant", section: de ? "Spielplatz-KI" : "Playground AI", run: () => { setOpen(false); onAsk(""); } }]
    : [];
  const all = [...openAi, ...cmds];
  const hits = needle ? all.filter((c) => c.label.toLowerCase().includes(needle) || c.section.toLowerCase().includes(needle)) : all;
  // KI-Zeile: erscheint sobald etwas getippt ist, immer als letzter Eintrag
  const rows: Array<Cmd | { id: "__ai"; section: "ki" }> = needle ? [...hits, { id: "__ai" as const, section: "ki" as const }] : hits;
  const clampedSel = Math.min(sel, Math.max(0, rows.length - 1));

  /** Zeile ins Auge gefasst — anwaermen. Idempotent: ein frischer Eintrag im
   *  Zwischenspeicher loest keine zweite Anfrage aus, ein geladener Chunk kein
   *  zweites Laden. Darum darf das ruhig bei jedem Pfeiltastendruck passieren. */
  const warmRow = (i: number) => {
    const r = rows[i];
    if (r && r.id !== "__ai") (r as Cmd).warm?.();
  };

  const runRow = (i: number) => {
    const r = rows[i];
    if (!r) return;
    if (r.id === "__ai") {
      // mit Assistent: Leiste zu, Frage laeuft im Chatfenster weiter
      if (onAsk) {
        setOpen(false);
        onAsk(q.trim());
      } else setAiAsked(q.trim());
    } else (r as Cmd).run();
  };

  if (!auth) return null;

  return (
    <>
      {variant === "hero" && (
        /* Hero-Leiste — der Einstiegspunkt des Dashboards, gross ueber den Kacheln */
        <button
          onClick={() => setOpen(true)}
          className="ring-focus cbar-hero"
          style={{
            display: "flex", alignItems: "center", gap: 14, width: "100%", height: 62,
            padding: "0 22px", borderRadius: 21, cursor: "text", textAlign: "left",
            border: "1px solid var(--glass-border)", background: "var(--panel-solid)",
            color: "var(--text-mute)", boxShadow: "var(--shadow)",
          }}
        >
          <span className="cbar-spark" style={{ display: "grid", placeItems: "center", flex: "none" }}>
            <Icon name="sparkle" size={21} weight="fill" style={{ color: "var(--accent-text)" }} />
          </span>
          <span style={{ flex: 1, minWidth: 0, fontSize: 15.5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {de ? "Suchen oder fragen — Karte, Projekte, Auswertungen, Spielplatz-KI…" : "Search or ask — map, projects, charts, playground AI…"}
          </span>
          <kbd className="font-mono" style={{ flex: "none", fontSize: 11.5, fontWeight: 600, padding: "3px 10px", borderRadius: 8, border: "1px solid var(--glass-border)", background: "var(--field)", color: "var(--text-mute)" }}>F</kbd>
        </button>
      )}

      {open &&
        createPortal(
          <div
            className="cbar-backdrop"
            style={{ position: "fixed", inset: 0, zIndex: 90, background: "color-mix(in srgb, var(--bg) 42%, transparent)", backdropFilter: "blur(3px)" }}
            onClick={() => setOpen(false)}
          >
            <div
              className="glass-strong cbar-sheet"
              role="dialog"
              aria-label={de ? "Kommandoleiste" : "Command bar"}
              onClick={(e) => e.stopPropagation()}
              style={{
                position: "absolute", top: 72, left: "50%", width: "min(660px, 94vw)",
                borderRadius: 20, boxShadow: "var(--shadow-lg)", overflow: "hidden",
                border: "1px solid var(--glass-border)",
              }}
            >
              {/* Eingabezeile */}
              <div style={{ display: "flex", alignItems: "center", gap: 11, padding: "15px 18px", borderBottom: "1px solid var(--glass-border)" }}>
                <span className="cbar-spark" style={{ display: "grid", placeItems: "center", flex: "none" }}>
                  <Icon name="sparkle" size={19} weight="fill" style={{ color: "var(--accent-text)" }} />
                </span>
                <input
                  ref={inputRef}
                  value={q}
                  onChange={(e) => { setQ(e.target.value); setSel(0); setAiAsked(null); }}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") setOpen(false);
                    else if (e.key === "ArrowDown") { e.preventDefault(); const n = Math.min(clampedSel + 1, rows.length - 1); setSel(n); warmRow(n); }
                    else if (e.key === "ArrowUp") { e.preventDefault(); const n = Math.max(clampedSel - 1, 0); setSel(n); warmRow(n); }
                    else if (e.key === "Enter") runRow(clampedSel);
                  }}
                  placeholder={de ? "Suchen oder fragen — Karte, Projekte, Auswertungen…" : "Search or ask — map, projects, charts…"}
                  style={{ flex: 1, border: "none", outline: "none", background: "transparent", fontSize: 15.5, color: "var(--text)" }}
                />
                <kbd className="font-mono" style={{ fontSize: 10, padding: "2px 7px", borderRadius: 6, border: "1px solid var(--glass-border)", color: "var(--text-mute)", flex: "none" }}>esc</kbd>
              </div>

              {/* Ergebnis-Liste */}
              <div className="scroll" style={{ maxHeight: "min(430px, 60vh)", overflowY: "auto", padding: "8px 8px 10px" }}>
                {rows.map((r, i) => {
                  if (r.id === "__ai") {
                    const on = i === clampedSel;
                    return (
                      <div key="__ai" style={{ marginTop: hits.length ? 6 : 0 }}>
                        <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: "var(--text-mute)", padding: "8px 12px 4px" }}>Spielplatz-KI</p>
                        <button
                          onClick={() => runRow(i)}
                          onMouseEnter={() => setSel(i)}
                          className="cbar-ai-row"
                          style={{
                            display: "flex", alignItems: "center", gap: 10, width: "100%", textAlign: "left",
                            padding: "11px 12px", borderRadius: 12, cursor: "pointer",
                            border: `1px solid ${on ? "var(--accent-ring)" : "var(--glass-border)"}`,
                            background: on ? "var(--accent-soft)" : "transparent",
                          }}
                        >
                          <span className="cbar-spark" style={{ display: "grid", placeItems: "center" }}>
                            <Icon name="sparkle" size={16} weight="fill" style={{ color: "var(--accent-text)" }} />
                          </span>
                          <span style={{ flex: 1, minWidth: 0, fontSize: 13.5, color: "var(--text)" }}>
                            {de ? "Spielplatz-KI fragen: " : "Ask the playground AI: "}
                            <em style={{ fontStyle: "normal", fontWeight: 700 }}>„{q.trim()}"</em>
                          </span>
                          {!onAsk && (
                            <span style={{ flex: "none", fontSize: 10, fontWeight: 700, letterSpacing: ".05em", padding: "3px 8px", borderRadius: 7, background: "var(--accent-soft)", color: "var(--accent-text)" }}>PREVIEW</span>
                          )}
                          {onAsk && <Icon name="arrow-right" size={13} style={{ color: "var(--accent-text)", flex: "none" }} />}
                        </button>
                        {aiAsked && (
                          <div className="cbar-ai-answer" style={{ margin: "8px 12px 4px", padding: "12px 14px", borderRadius: 12, border: "1px dashed var(--accent-ring)", background: "var(--accent-soft)" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                              <span className="cbar-spark"><Icon name="sparkle" size={14} weight="fill" style={{ color: "var(--accent-text)" }} /></span>
                              <span style={{ fontSize: 12, fontWeight: 700, color: "var(--accent-text)" }}>{de ? "In Vorbereitung" : "Coming soon"}</span>
                            </div>
                            <p style={{ fontSize: 12.5, color: "var(--text-dim)", lineHeight: 1.55 }}>
                              {de
                                ? "Die Spielplatz-KI wird gerade eingebaut. Bald steuert sie von hier aus Karte, Diagramme, Mängel und Exporte — per Frage statt per Klickpfad."
                                : "The playground AI is being wired up. Soon it will drive the map, charts, defects and exports from right here."}
                            </p>
                            <div style={{ display: "flex", gap: 5, marginTop: 10 }}>
                              {[0, 1, 2].map((k) => (
                                <span key={k} className="cbar-shimmer" style={{ height: 7, borderRadius: 4, flex: k === 0 ? 3 : k === 1 ? 5 : 2, animationDelay: `${k * 0.18}s` }} />
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  }
                  const c = r as Cmd;
                  const on = i === clampedSel;
                  const firstOfSection = i === 0 || (rows[i - 1] as Cmd).section !== c.section;
                  return (
                    <div key={c.id}>
                      {firstOfSection && (
                        <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: "var(--text-mute)", padding: "8px 12px 4px" }}>{c.section}</p>
                      )}
                      <button
                        onClick={() => runRow(i)}
                        onMouseEnter={() => { setSel(i); warmRow(i); }}
                        onFocus={() => warmRow(i)}
                        style={{
                          display: "flex", alignItems: "center", gap: 10, width: "100%", textAlign: "left",
                          padding: "9px 12px", borderRadius: 11, cursor: "pointer", border: "none",
                          background: on ? "var(--accent-soft)" : "transparent",
                        }}
                      >
                        <span style={{ width: 3, height: 16, borderRadius: 2, background: on ? "var(--accent-text)" : "transparent", flex: "none", transition: "background .12s" }} />
                        <Icon name={c.icon} size={16} style={{ color: on ? "var(--accent-text)" : "var(--text-mute)", flex: "none" }} />
                        <span style={{ flex: 1, minWidth: 0, fontSize: 13.5, fontWeight: on ? 650 : 500, color: "var(--text)" }}>{c.label}</span>
                        {c.hint && <span style={{ flex: "none", fontSize: 11, color: "var(--text-mute)" }}>{c.hint}</span>}
                        {on && <Icon name="arrow-right" size={13} style={{ color: "var(--accent-text)", flex: "none" }} />}
                      </button>
                    </div>
                  );
                })}
                {!rows.length && (
                  <p style={{ padding: "22px 12px", fontSize: 13, color: "var(--text-mute)", textAlign: "center" }}>{de ? "Nichts gefunden." : "No matches."}</p>
                )}
              </div>

              {/* Fußzeile */}
              <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 16px", borderTop: "1px solid var(--glass-border)", fontSize: 10.5, color: "var(--text-mute)" }}>
                <span><kbd className="font-mono">↑↓</kbd> {de ? "wählen" : "select"}</span>
                <span><kbd className="font-mono">↵</kbd> {de ? "öffnen" : "open"}</span>
                <span style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 5 }}>
                  <Icon name="sparkle" size={11} weight="fill" style={{ color: "var(--accent-text)" }} />
                  SpielplatzGIS
                </span>
              </div>
            </div>
          </div>,
          portalRoot(),
        )}

      <style>{`
        .cbar-backdrop { animation: cbar-fade .18s ease-out both; }
        .cbar-sheet { animation: cbar-drop .32s cubic-bezier(.22,1,.36,1) both; transform-origin: top center; }
        @keyframes cbar-fade { from { opacity: 0; } to { opacity: 1; } }
        @keyframes cbar-drop { from { opacity: 0; transform: translate(-50%, -12px) scale(.975); } to { opacity: 1; transform: translate(-50%, 0) scale(1); } }
        .cbar-spark { animation: cbar-pulse 2.6s ease-in-out infinite; }
        @keyframes cbar-pulse { 0%,100% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.14); opacity: .82; } }
        .cbar-ai-answer { animation: cbar-drop2 .3s cubic-bezier(.22,1,.36,1) both; }
        @keyframes cbar-drop2 { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: translateY(0); } }
        .cbar-shimmer { background: linear-gradient(90deg, var(--accent-ring) 25%, var(--accent-text) 50%, var(--accent-ring) 75%); background-size: 200% 100%; animation: cbar-sh 1.6s linear infinite; opacity: .55; }
        @keyframes cbar-sh { from { background-position: 200% 0; } to { background-position: -200% 0; } }
        .cbar-hero { transition: border-color .25s, box-shadow .25s, transform .25s cubic-bezier(.22,1,.36,1); animation: cbar-breathe 5s ease-in-out infinite; }
        .cbar-hero:hover { border-color: var(--accent-ring); box-shadow: 0 0 0 4px var(--accent-soft), var(--shadow-lg); transform: translateY(-1px); }
        @keyframes cbar-breathe { 0%,100% { box-shadow: var(--shadow); } 50% { box-shadow: 0 0 0 5px var(--accent-soft), var(--shadow); } }
        @media (prefers-reduced-motion: reduce) { .cbar-backdrop, .cbar-sheet, .cbar-spark, .cbar-ai-answer, .cbar-shimmer, .cbar-hero { animation: none !important; } }
      `}</style>
    </>
  );
}
